import { createSlice, createAsyncThunk } from "@reduxjs/toolkit"
import { reportService } from "../../services/api"

const initialState = {
  filters: {
    date_from: null,
    date_to: null,
    payment_method: [],
    payment_status: [],
    order_status: [],
    coupon_code: [],
    coupon_type: [],
    approval: null,
    currency: [],
    search: "",
    granularity: "day"
  },
  overview: null,
  payments: null,
  coupons: null,
  users: null,
  transactions: [],
  transactionsPagination: {
    current_page: 1,
    last_page: 1,
    per_page: 25,
    total: 0
  },
  loading: {
    overview: false,
    payments: false,
    coupons: false,
    users: false,
    transactions: false
  },
  error: null
}

// Thunks - cada um recebe os filtros já montados
export const fetchOverview = createAsyncThunk(
  "reports/fetchOverview",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportService.getOverview(filters)
      return response.data.obj
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Falha ao carregar a visão geral"
      )
    }
  }
)

export const fetchPayments = createAsyncThunk(
  "reports/fetchPayments",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportService.getPayments(filters)
      return response.data.obj
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Falha ao carregar pagamentos"
      )
    }
  }
)

export const fetchCouponsReport = createAsyncThunk(
  "reports/fetchCoupons",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportService.getCoupons(filters)
      return response.data.obj
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Falha ao carregar cupons"
      )
    }
  }
)

export const fetchUsersReport = createAsyncThunk(
  "reports/fetchUsers",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportService.getUsers(filters)
      return response.data.obj
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Falha ao carregar usuários"
      )
    }
  }
)

export const fetchTransactionsReport = createAsyncThunk(
  "reports/fetchTransactions",
  async (filters = {}, { rejectWithValue }) => {
    try {
      const response = await reportService.getTransactions(filters)
      return response.data.obj
    } catch (error) {
      return rejectWithValue(
        error.response?.data?.message || "Falha ao carregar pedidos"
      )
    }
  }
)

const reportsSlice = createSlice({
  name: "reports",
  initialState,
  reducers: {
    setFilters: (state, action) => {
      state.filters = { ...state.filters, ...action.payload }
    },
    resetFilters: (state) => {
      state.filters = { ...initialState.filters }
    },
    clearError: (state) => {
      state.error = null
    }
  },
  extraReducers: (builder) => {
    builder
      // Overview
      .addCase(fetchOverview.pending, (state) => {
        state.loading.overview = true
        state.error = null
      })
      .addCase(fetchOverview.fulfilled, (state, action) => {
        state.loading.overview = false
        state.overview = action.payload
      })
      .addCase(fetchOverview.rejected, (state, action) => {
        state.loading.overview = false
        state.error = action.payload
      })
      // Payments
      .addCase(fetchPayments.pending, (state) => {
        state.loading.payments = true
        state.error = null
      })
      .addCase(fetchPayments.fulfilled, (state, action) => {
        state.loading.payments = false
        state.payments = action.payload
      })
      .addCase(fetchPayments.rejected, (state, action) => {
        state.loading.payments = false
        state.error = action.payload
      })
      // Coupons
      .addCase(fetchCouponsReport.pending, (state) => {
        state.loading.coupons = true
        state.error = null
      })
      .addCase(fetchCouponsReport.fulfilled, (state, action) => {
        state.loading.coupons = false
        state.coupons = action.payload
      })
      .addCase(fetchCouponsReport.rejected, (state, action) => {
        state.loading.coupons = false
        state.error = action.payload
      })
      // Users
      .addCase(fetchUsersReport.pending, (state) => {
        state.loading.users = true
        state.error = null
      })
      .addCase(fetchUsersReport.fulfilled, (state, action) => {
        state.loading.users = false
        state.users = action.payload
      })
      .addCase(fetchUsersReport.rejected, (state, action) => {
        state.loading.users = false
        state.error = action.payload
      })
      // Transactions
      .addCase(fetchTransactionsReport.pending, (state) => {
        state.loading.transactions = true
        state.error = null
      })
      .addCase(fetchTransactionsReport.fulfilled, (state, action) => {
        state.loading.transactions = false
        state.transactions = action.payload?.transactions || []
        if (action.payload?.pagination) {
          state.transactionsPagination = action.payload.pagination
        }
      })
      .addCase(fetchTransactionsReport.rejected, (state, action) => {
        state.loading.transactions = false
        state.error = action.payload
      })
  }
})

export const { setFilters, resetFilters, clearError } = reportsSlice.actions

export default reportsSlice.reducer
